/**
 * Cookie Utilities
 * Provides helper functions for cookie management with security best practices
 */

// Set a cookie with optional security settings
export const setCookie = (name, value, options = {}) => {
  let cookieString = `${name}=${encodeURIComponent(value)}`;
  
  const defaultOptions = {
    path: '/',
    secure: window.location.protocol === 'https:', // Only secure in production
    sameSite: 'Strict',
    ...options
  };

  // Add expiration (default 7 days)
  if (defaultOptions.expires) {
    if (defaultOptions.expires instanceof Date) {
      cookieString += `; expires=${defaultOptions.expires.toUTCString()}`;
    } else if (typeof defaultOptions.expires === 'number') {
      const date = new Date();
      date.setTime(date.getTime() + (defaultOptions.expires * 24 * 60 * 60 * 1000));
      cookieString += `; expires=${date.toUTCString()}`;
    }
  } else {
    // Default 7 days
    const date = new Date();
    date.setTime(date.getTime() + (7 * 24 * 60 * 60 * 1000));
    cookieString += `; expires=${date.toUTCString()}`;
  }

  if (defaultOptions.path) {
    cookieString += `; path=${defaultOptions.path}`;
  }

  if (defaultOptions.secure) {
    cookieString += `; secure`;
  }

  if (defaultOptions.sameSite) {
    cookieString += `; samesite=${defaultOptions.sameSite}`;
  }

  if (defaultOptions.httpOnly) {
    cookieString += `; httponly`;
  }

  document.cookie = cookieString;
};

// Get a cookie value by name
export const getCookie = (name) => {
  const nameEQ = name + "=";
  const ca = document.cookie.split(';');
  
  for (let i = 0; i < ca.length; i++) {
    let c = ca[i];
    while (c.charAt(0) === ' ') c = c.substring(1, c.length);
    if (c.indexOf(nameEQ) === 0) {
      return decodeURIComponent(c.substring(nameEQ.length, c.length));
    }
  }
  return null;
};

// Remove a cookie
export const removeCookie = (name, path = '/') => {
  document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=${path};`;
};

// Check if cookies are enabled
export const areCookiesEnabled = () => {
  try {
    document.cookie = "cookietest=1";
    const ret = document.cookie.indexOf("cookietest=") !== -1;
    document.cookie = "cookietest=1; expires=Thu, 01-Jan-1970 00:00:01 GMT";
    return ret;
  } catch (e) {
    return false;
  }
};

// Auth specific cookie helpers
export const setAuthToken = (token, options = {}) => {
  setCookie('access_token', token, {
    expires: 1, // 1 day for access token
    secure: true,
    sameSite: 'Strict',
    ...options
  });
};

export const setRefreshToken = (token, options = {}) => {
  setCookie('refresh_token', token, {
    expires: 1, 
    secure: true,
    sameSite: 'Strict',
    ...options
  });
};
export const setUserinfo = (user, options = {}) => {
  setCookie('user_info', JSON.stringify(user), {
    expires: 1, 
    secure: true,
    sameSite: 'Strict',
    ...options
  });
};
export const setAuthUser = (user, options = {}) => {
  setCookie('authUser', JSON.stringify(user), {
    expires: 1, 
    secure: true,
    sameSite: 'Strict',
    ...options
  });
};

export const getAuthToken = () => {
  return getCookie('access_token');
};

export const getRefreshToken = () => {
  return getCookie('refresh_token');
};

export const getAuthUser = () => {
  try {
    const userStr = getCookie('authUser');
    return userStr ? JSON.parse(userStr) : null;
  } catch (error) {
    console.error("Error parsing user data from cookie:", error);
    return null;
  }
};

export const clearAuthCookies = () => {
  removeCookie('access_token');
  removeCookie('refresh_token');
  removeCookie('authUser');
  removeCookie('user_info');
};

// Check if user is authenticated using cookies
export const isAuthenticatedByCookie = () => {
  const token = getAuthToken();
  const user = getAuthUser();
  return !!(token && user);
};
